'use client';

import { useState } from 'react';

import { Body } from '@depop/web-thrift-ui/Typography';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { BasedOnRecentViewsModal } from './BasedOnRecentViewsModal';
import { BasedOnRecentViewsPlaceholder } from './BasedOnRecentViewsPlaceholder';
import { BasedOnRecentViewsProductCard } from './BasedOnRecentViewsProductCard';
import styles from './styles.module.css';

import { ButtonMinimal } from '@/components/ButtonMinimal';
import { ModuleHeader, ModuleHeaderSize } from '@/components/ModuleHeader';
import { ScrollableItemRow } from '@/components/ScrollableItemRow';
import {
  ActivityTrackerEventType,
  ModuleInteractionType,
  ModuleNames,
} from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import {
  BASED_ON_RECENT_VIEWS_LIMIT,
  BASED_ON_RECENT_VIEWS_LIMIT_EXTENDED,
  BASED_ON_RECENT_VIEWS_VISIBLE_COUNT,
} from '@/modules/basedOnRecentViews/constants';
import { useBasedOnRecentViews } from '@/modules/basedOnRecentViews/hooks/useBasedOnRecentViews';
import { VARIANT_IDENTIFIER } from '@/modules/experiments/config';
import { useExperiments } from '@/modules/experiments/useExperiments';
import { type BasedOnRecentViewsRecommendationsProduct } from '@/modules/product/aliases';
import { PRODUCT_INTERACTION_TYPES } from '@/modules/product/constants';
import { useProductTracking } from '@/modules/product/hooks/useProductTracking';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

export function BasedOnRecentViews() {
  const t = useTranslations('product');
  const tCommon = useTranslations('common');
  const pathname = usePathname();
  const [, currentUser] = useCurrentUser();
  const { sendProductInteractionActionATEvent } = useProductTracking();
  const { sendActivityTrackerEvent } = useActivityTracker({
    userId: currentUser?.id,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [{ web_8958_increase_products_borv_modal }, bucketBorvModal] =
    useExperiments(['web_8958_increase_products_borv_modal'], {
      deferred: true,
    });

  const isIncreaseProductsBorvModalExperiment =
    web_8958_increase_products_borv_modal === VARIANT_IDENTIFIER;

  const { products, isLoading } = useBasedOnRecentViews({
    limit: isIncreaseProductsBorvModalExperiment
      ? BASED_ON_RECENT_VIEWS_LIMIT_EXTENDED
      : BASED_ON_RECENT_VIEWS_LIMIT,
  });

  const isPDP = pathname.includes('/products/');
  const interactionType = isPDP
    ? PRODUCT_INTERACTION_TYPES.BASED_ON_RECENT_VIEWS_PDP
    : PRODUCT_INTERACTION_TYPES.BASED_ON_RECENT_VIEWS_HOME;

  if (isLoading) {
    return <BasedOnRecentViewsPlaceholder />;
  }

  if (!products.length) {
    return null;
  }

  const visibleProducts = products.slice(
    0,
    BASED_ON_RECENT_VIEWS_VISIBLE_COUNT
  );
  const showSeeMore = products.length > BASED_ON_RECENT_VIEWS_VISIBLE_COUNT;

  function handleProductClick(
    product: BasedOnRecentViewsRecommendationsProduct
  ) {
    sendProductInteractionActionATEvent(product.id, interactionType, {
      brand: product.brand_name,
      isBoosted: product.is_boosted,
    });
  }

  function handleSeeMoreClick() {
    const moduleName = isPDP
      ? ModuleNames.BasedOnRecentViewsPDP
      : ModuleNames.BasedOnRecentViewsHomepage;
    sendActivityTrackerEvent(
      ActivityTrackerEventType.MODULE_INTERACTION_ACTION,
      {
        moduleName,
        interactionType: ModuleInteractionType.BasedOnRecentViewsSeeMore,
        schemaVersion: '1.0',
      }
    );
    bucketBorvModal();
    setIsModalOpen(true);
  }

  return (
    <section>
      <ModuleHeader
        title={t('BasedOnYourRecentViews')}
        size={ModuleHeaderSize.Large}
      >
        {showSeeMore && (
          <ButtonMinimal
            onClick={handleSeeMoreClick}
            className={styles.seeMoreText}
          >
            <Body as="span" variant="sm-regular">
              {tCommon('SeeMore')}
            </Body>
          </ButtonMinimal>
        )}
      </ModuleHeader>
      <ScrollableItemRow
        itemsPerRow={BASED_ON_RECENT_VIEWS_VISIBLE_COUNT}
        minItemWidth={144}
        className={styles.scrollableItemRow}
      >
        {visibleProducts.map((product) => (
          <li key={product.id}>
            <BasedOnRecentViewsProductCard
              product={product}
              onClick={handleProductClick}
            />
          </li>
        ))}
      </ScrollableItemRow>

      {showSeeMore && (
        <BasedOnRecentViewsModal
          title={t('BasedOnYourRecentViews')}
          isOpen={isModalOpen}
          setOpen={setIsModalOpen}
          products={products}
          onProductClick={handleProductClick}
        />
      )}
    </section>
  );
}
